import { useCallback, useEffect, useState } from "react";
import { api, Unauthorised, type Today } from "./api.ts";
import { colors, sans, serif, tokens } from "./theme.ts";
import { Blobs, GlassButton, GlassCard, GlassFloat, inputStyle } from "./components/glass.tsx";
import { Nav, type Tab } from "./components/Nav.tsx";
import { CaptureButton, CaptureSheet } from "./components/Capture.tsx";
import { PullToRefresh } from "./components/PullToRefresh.tsx";
import { TaskSheet } from "./components/TaskSheet.tsx";
import { ContainerSheet } from "./components/ContainerSheet.tsx";
import { Home } from "./screens/Home.tsx";
import { Board } from "./screens/Board.tsx";
import { Calendar } from "./screens/Calendar.tsx";
import { Week } from "./screens/Week.tsx";
import { Status } from "./screens/Status.tsx";

export default function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [tab, setTab] = useState<Tab>("home");
  const [today, setToday] = useState<Today | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [toast, setToast] = useState(false);
  // Bumped whenever something is changed or the screen is pulled down, so the
  // screens that fetch their own data (Board and the others) fetch it again.
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    setVersion((v) => v + 1);
    try { setToday(await api.today()); setError(null); }
    catch (e) {
      if (e instanceof Unauthorised) { setAuthed(false); return; }
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => { api.me().then((r) => setAuthed(r.authenticated)).catch(() => setAuthed(false)); }, []);
  useEffect(() => { if (authed) load(); }, [authed, load]);

  const lock = async () => {
    try { await api.logout(); } catch { /* the cookie is the server's to clear; the screen locks regardless */ }
    // The service worker keeps the last brief for offline reading; locking drops it.
    try { await caches.delete("mc2-data-v1"); } catch { /* no cache API */ }
    setToday(null); setAuthed(false);
  };

  if (authed === null) return <Shell><p style={{ color: colors.ink, padding: 24 }}>Opening…</p></Shell>;
  if (!authed) return <Login onIn={() => setAuthed(true)} />;

  return (
    <Shell>
      <PullToRefresh onRefresh={load} enabled={!openId && !openKey}>
        {error && <p style={{ color: colors.ink, fontSize: 14, padding: "16px 16px 0", margin: 0 }}>{error}</p>}
        {tab === "home" && today && <Home data={today} onOpen={setOpenId} onOpenContainer={setOpenKey} onChanged={load} onLock={lock} />}
        {tab === "board" && <Board version={version} onOpen={setOpenId} onOpenContainer={setOpenKey} />}
        {tab === "calendar" && <Calendar version={version} onOpen={setOpenId} />}
        {tab === "week" && <Week version={version} onOpen={setOpenId} onChanged={load} />}
        {tab === "status" && <Status version={version} />}
      </PullToRefresh>

      {openKey && !openId && (
        <ContainerSheet key={openKey} containerKey={openKey} onClose={() => setOpenKey(null)} onOpenTask={setOpenId} />
      )}
      {openId && <TaskSheet key={openId} id={openId} onClose={() => setOpenId(null)} onChanged={load} onOpen={setOpenId} />}

      {!openId && !openKey && <CaptureButton onClick={() => setCapturing(true)} />}
      {capturing && (
        <CaptureSheet
          onClose={() => setCapturing(false)}
          onSaved={() => { setToast(true); window.setTimeout(() => setToast(false), 2500); }}
        />
      )}
      {toast && (
        <div role="status" style={{ position: "fixed", left: 0, right: 0, top: "calc(env(safe-area-inset-top) + 12px)", display: "flex", justifyContent: "center", zIndex: 50 }}>
          <GlassFloat style={{ padding: "10px 18px", fontSize: 14, color: colors.ink }}>Saved to the journal</GlassFloat>
        </div>
      )}

      <Nav active={tab} onNavigate={setTab} />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    // `isolation: isolate` gives this its own stacking context so Blobs's
    // negative z-index resolves inside it, not behind the body background.
    <div style={{
      isolation: "isolate", fontFamily: sans, color: colors.ink, minHeight: "100dvh",
      boxSizing: "border-box", maxWidth: 560, margin: "0 auto",
      paddingTop: "env(safe-area-inset-top)", paddingBottom: "calc(var(--nav-clear) + 72px)",
      paddingLeft: "env(safe-area-inset-left)", paddingRight: "env(safe-area-inset-right)",
    }}>
      <Blobs />
      {children}
    </div>
  );
}

function Login({ onIn }: { onIn: () => void }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await api.login(value); onIn(); }
    catch (err: any) { setError(err.message); }
    finally { setBusy(false); }
  };

  return (
    <Shell>
      <div style={{ padding: `22dvh ${tokens.space.gutter}px 0` }}>
        <GlassCard padding={tokens.space.cardPad + 4}>
          <div style={{ fontFamily: serif, fontSize: tokens.type.screenTitle.size, color: colors.ink }}>Mission Control</div>
          <form onSubmit={submit} style={{ marginTop: 16 }}>
            <input
              type="password" autoFocus value={value} onChange={(e) => setValue(e.target.value)}
              placeholder="passphrase" autoComplete="current-password" style={inputStyle}
            />
            <div style={{ marginTop: 10 }}>
              <GlassButton variant="primary" fullWidth type="submit" disabled={busy || !value}>{busy ? "…" : "Open"}</GlassButton>
            </div>
          </form>
          {error && <p style={{ color: colors.ink, fontSize: 14, margin: "12px 0 0" }}>{error}</p>}
        </GlassCard>
      </div>
    </Shell>
  );
}
